<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-buzz-mention-input">
    <oxd-buzz-post-input
      ref="buzzPostInputRef"
      v-bind="$attrs"
      :model-value="modelValue"
      :rules="rules"
      :disabled="disabled"
      :placeholder="placeholder"
      @update:model-value="onModelUpdate"
    >
      <slot />
    </oxd-buzz-post-input>
    <ul
      v-if="showSuggestions"
      class="orangehrm-buzz-mention-input-suggestions"
      @mousedown.prevent
    >
      <li
        v-for="(employee, index) in suggestions"
        :key="employee.empNumber"
        :class="{
          'orangehrm-buzz-mention-input-suggestion': true,
          '--active': index === activeSuggestionIndex,
        }"
        @click="selectSuggestion(employee)"
      >
        <img
          class="orangehrm-buzz-mention-input-suggestion-photo"
          :src="employeePhotoUrl(employee.empNumber)"
          alt=""
        />
        <div class="orangehrm-buzz-mention-input-suggestion-meta">
          <span class="orangehrm-buzz-mention-input-suggestion-name">
            {{ employee.fullName }}
          </span>
          <span class="orangehrm-buzz-mention-input-suggestion-username">
            @{{ employee.mentionUserName }}
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import {nextTick, onBeforeUnmount, ref, watch} from 'vue';
import {OxdBuzzPostInput} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';
import {
  formatBuzzMentionToken,
  formatEmployeeDisplayName,
  resolveBuzzMentionUserName,
} from '@/orangehrmBuzzPlugin/util/formatBuzzMentions';

export default {
  name: 'BuzzMentionInput',

  components: {
    'oxd-buzz-post-input': OxdBuzzPostInput,
  },

  inheritAttrs: false,

  props: {
    modelValue: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: null,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    submitOnEnter: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'keydown'],

  setup(props, context) {
    const buzzPostInputRef = ref(null);
    const suggestions = ref([]);
    const showSuggestions = ref(false);
    const activeSuggestionIndex = ref(0);
    const mentionQuery = ref('');
    const mentionStart = ref(null);
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/buzz/employees',
    );

    let searchTimer = null;
    let textAreaEl = null;

    const employeePhotoUrl = (empNumber) => {
      return `../pim/viewPhoto/empNumber/${empNumber}`;
    };

    const mapSuggestion = (employee) => ({
      ...employee,
      fullName: formatEmployeeDisplayName(employee),
      mentionUserName: resolveBuzzMentionUserName(employee),
    });

    const getTextArea = () => {
      const component = buzzPostInputRef.value;
      if (!component) {
        return null;
      }

      return component.textArea ?? component.$el?.querySelector('textarea');
    };

    const syncTextAreaHeight = () => {
      const textArea = getTextArea();
      if (!textArea) {
        return;
      }

      textArea.dispatchEvent(new Event('input', {bubbles: true}));
    };

    const closeSuggestions = () => {
      showSuggestions.value = false;
      suggestions.value = [];
      mentionQuery.value = '';
      mentionStart.value = null;
      activeSuggestionIndex.value = 0;
    };

    const updateMentionState = (value, cursorPosition) => {
      const text = value ?? '';
      const textBeforeCursor = text.slice(0, cursorPosition);
      const match = textBeforeCursor.match(/(?:^|\s)@([^\s@[\]|]{0,50})$/);

      if (!match) {
        closeSuggestions();
        return;
      }

      mentionStart.value = cursorPosition - match[1].length - 1;
      mentionQuery.value = match[1];

      if (searchTimer) {
        clearTimeout(searchTimer);
      }

      if (mentionQuery.value.trim() === '') {
        suggestions.value = [];
        showSuggestions.value = false;
        return;
      }

      searchTimer = setTimeout(async () => {
        try {
          const response = await http.getAll({
            nameOrId: mentionQuery.value.trim(),
            limit: 10,
          });
          suggestions.value = response.data.data.map(mapSuggestion);
          activeSuggestionIndex.value = 0;
          showSuggestions.value = suggestions.value.length > 0;
        } catch {
          closeSuggestions();
        }
      }, 250);
    };

    const onModelUpdate = (value) => {
      context.emit('update:modelValue', value);
    };

    const selectSuggestion = async (employee) => {
      const currentValue = props.modelValue ?? '';
      const start = mentionStart.value;
      const textArea = getTextArea();
      const cursorPosition = textArea?.selectionStart ?? currentValue.length;

      if (start === null || start < 0) {
        closeSuggestions();
        return;
      }

      const token = formatBuzzMentionToken(
        employee.empNumber,
        employee.mentionUserName,
      );
      const nextValue =
        currentValue.slice(0, start) +
        token +
        ' ' +
        currentValue.slice(cursorPosition);

      context.emit('update:modelValue', nextValue);
      closeSuggestions();

      const nextCursor = start + token.length + 1;
      await nextTick();
      const nextTextArea = getTextArea();
      if (!nextTextArea) {
        return;
      }

      syncTextAreaHeight();
      nextTextArea.focus();
      nextTextArea.setSelectionRange(nextCursor, nextCursor);
    };

    const onKeydown = async ($event) => {
      if (showSuggestions.value && suggestions.value.length) {
        if ($event.key === 'ArrowDown') {
          $event.preventDefault();
          activeSuggestionIndex.value =
            (activeSuggestionIndex.value + 1) % suggestions.value.length;
          return;
        }
        if ($event.key === 'ArrowUp') {
          $event.preventDefault();
          activeSuggestionIndex.value =
            (activeSuggestionIndex.value - 1 + suggestions.value.length) %
            suggestions.value.length;
          return;
        }
        if ($event.key === 'Enter' || $event.key === 'Tab') {
          $event.preventDefault();
          await selectSuggestion(
            suggestions.value[activeSuggestionIndex.value],
          );
          return;
        }
        if ($event.key === 'Escape') {
          closeSuggestions();
          return;
        }
      }

      if (
        props.submitOnEnter &&
        $event.key === 'Enter' &&
        !$event.shiftKey &&
        !props.disabled &&
        props.modelValue?.trim()
      ) {
        $event.preventDefault();
        getTextArea()?.closest('form')?.requestSubmit();
        return;
      }

      context.emit('keydown', $event);
    };

    const onTextAreaBlur = () => {
      setTimeout(closeSuggestions, 150);
    };

    const onTextAreaInput = ($event) => {
      const textArea = $event.target;
      updateMentionState(textArea.value, textArea.selectionStart ?? 0);
    };

    const attachTextAreaListeners = () => {
      textAreaEl?.removeEventListener('keydown', onKeydown);
      textAreaEl?.removeEventListener('blur', onTextAreaBlur);
      textAreaEl?.removeEventListener('input', onTextAreaInput);

      textAreaEl = getTextArea();
      textAreaEl?.addEventListener('keydown', onKeydown);
      textAreaEl?.addEventListener('blur', onTextAreaBlur);
      textAreaEl?.addEventListener('input', onTextAreaInput);
    };

    watch(
      buzzPostInputRef,
      () => {
        nextTick(attachTextAreaListeners);
      },
      {immediate: true},
    );

    watch(
      () => props.modelValue,
      () => {
        nextTick(syncTextAreaHeight);
      },
    );

    onBeforeUnmount(() => {
      textAreaEl?.removeEventListener('keydown', onKeydown);
      textAreaEl?.removeEventListener('blur', onTextAreaBlur);
      textAreaEl?.removeEventListener('input', onTextAreaInput);
    });

    return {
      buzzPostInputRef,
      suggestions,
      showSuggestions,
      activeSuggestionIndex,
      employeePhotoUrl,
      onModelUpdate,
      selectSuggestion,
    };
  },
};
</script>

<style src="./buzz-mention-input.scss" lang="scss" scoped></style>
